import { TONE_VAR, type Tone } from "@/features/utils/colors";
import { useIsDark } from "@/features/utils/theme";
import type { CommonLineProps, DefaultSeries } from "@nivo/line";
import { useMemo } from "react";

/**
 * The one nivo theme, read from the design tokens.
 *
 * Every value is resolved to a concrete colour rather than handed to nivo as
 * `var(--x)`: nivo computes with some of them (a pie's border is its slice
 * colour "darker", a legend symbol is drawn from the series colour), and a
 * `var()` it cannot parse comes out black. Resolving means reading again when
 * the theme changes, which is what `useIsDark` is for — the colours were read
 * once before, so a chart drawn in light mode kept its light grid in dark.
 */

/** nivo's theme type, which its core package declares but does not export. */
type PartialTheme = CommonLineProps<DefaultSeries>["theme"];

export const CHART_FONT = "'IBM Plex Sans', system-ui, sans-serif";

const read = (styles: CSSStyleDeclaration, name: string) =>
  styles.getPropertyValue(name).trim();

/**
 * Series colours, in order: the brand gradient (`--chart-1…5`), then three
 * more for the admin charts that can show up to eight providers or targets.
 * Those three are tokens too, and far enough from the first five to be told
 * apart on a line.
 */
const SERIES_VARS = [
  "--chart-1",
  "--chart-2",
  "--chart-3",
  "--chart-4",
  "--chart-5",
  "--chart-6",
  "--success",
  "--muted-foreground",
];

export interface ChartTokens {
  text: string;
  grid: string;
  foreground: string;
  /** The surface a chart sits on — what a hollow point is filled with. */
  surface: string;
  series: string[];
  tone: (tone: Tone) => string;
}

export function useChartTheme(): { theme: PartialTheme; tokens: ChartTokens } {
  const isDark = useIsDark();

  return useMemo(() => {
    const styles = getComputedStyle(document.documentElement);
    const tokens: ChartTokens = {
      text: read(styles, "--muted-foreground"),
      grid: read(styles, "--chart-grid"),
      foreground: read(styles, "--foreground"),
      surface: read(styles, "--card"),
      series: SERIES_VARS.map((name) => read(styles, name)),
      tone: (tone) => read(styles, TONE_VAR[tone]),
    };

    const theme: PartialTheme = {
      text: { fontFamily: CHART_FONT, fontSize: 11, fill: tokens.text },
      grid: { line: { stroke: tokens.grid, strokeWidth: 1 } },
      axis: {
        domain: { line: { stroke: "transparent" } },
        ticks: {
          line: { stroke: tokens.grid },
          text: { fontFamily: CHART_FONT, fontSize: 11, fill: tokens.text },
        },
        legend: {
          text: { fontFamily: CHART_FONT, fontSize: 11, fill: tokens.text },
        },
      },
      legends: {
        text: { fontFamily: CHART_FONT, fontSize: 11, fill: tokens.text },
      },
      crosshair: {
        line: { stroke: tokens.text, strokeOpacity: 0.5, strokeWidth: 1 },
      },
    };

    return { theme, tokens };
    // The tokens only change with the theme class: `isDark` is the trigger.
  }, [isDark]);
}

/** A series colour by position, cycling past the eighth. */
export const seriesColor = (tokens: ChartTokens, index: number) =>
  tokens.series[index % tokens.series.length];
